import React, { useMemo, useState } from "react";
import { CheckCircle, DoneAll, Timer, SoupKitchen } from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  useGetRestaurantTicketsQuery,
  useUpdateItemStatusMutation,
} from "../Store/Services/CartService";
import { msSince } from "../Utils/apiDate";
import styles from "./KitchenPage.module.css";
import { useDispatch } from "react-redux";

const timeAgo = (iso) => {
  if (!iso) return "";
  // msSince handles the API's "YYYY-MM-DD HH:mm:ss.S" timestamps, which
  // new Date() either misreads as local time or rejects outright.
  const diff = Math.floor(msSince(iso) / 60000);
  if (diff <= 0) return "just now";
  return `${diff} min`;
};

const STATION_STORAGE_KEY = "pos:kitchen-stations";

const loadSelectedStations = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STATION_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};


const getStatusCode = (line) => String(line?.statusCode || "").toUpperCase();
const getCategoryName = (line) =>
  line?.prodCatDisplayName || line?.prodCatName || "General";

const isKitchenActiveLine = (line) => {
  const code = getStatusCode(line);
  return code === "SENT_TO_KITCHEN" || code === "PREPARING" || code === "READY";
};
const KitchenPage = () => {
  const dispatch = useDispatch();
  const payload = useMemo(
    () => ({
      statusIds: [2, 3, 4],
      categoryIds: [],
      tableIds: [],
    }),
    []
  );

  const { data, isLoading, isFetching } = useGetRestaurantTicketsQuery(payload);
  const [updateItemStatus] = useUpdateItemStatusMutation();
  // Empty = show every station. Otherwise only the picked categories are shown.
  const [selectedStations, setSelectedStations] = useState(loadSelectedStations);

  const persistStations = (next) => {
    try {
      localStorage.setItem(STATION_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore storage failures
    }
  };

  const toggleStation = (station) => {
    setSelectedStations((prev) => {
      const next = prev.includes(station)
        ? prev.filter((s) => s !== station)
        : [...prev, station];
      persistStations(next);
      return next;
    });
  };

  const clearStations = () => {
    setSelectedStations([]);
    persistStations([]);
  };

  const updateStatus = async (line, statusId) => {
    if (!line?.orderDetId) {
      return true;
    }

    try {
      await updateItemStatus({
        orderDetId: line.orderDetId,
        statusId,
      }).unwrap();
      return true;
    } catch {
      toast.error("Unable to update item status", {
        position: "bottom-right",
      });
      return false;
    }
  };

  const acceptLine = async (line) => {
    if (getStatusCode(line) !== "SENT_TO_KITCHEN") {
      return;
    }

    const ok = await updateStatus(line, 3);
    if (!ok) return;
  };

  const readyLine = async (line) => {
    const code = getStatusCode(line);
    if (code !== "SENT_TO_KITCHEN" && code !== "PREPARING") {
      return;
    }

    const ok = await updateStatus(line, 4);
    if (!ok) return;
  };

  const acceptAllInTicket = async (ticket) => {
    const candidates = ticket.items.filter(
      (line) => getStatusCode(line) === "SENT_TO_KITCHEN"
    );
    if (!candidates.length) return;

    await Promise.all(candidates.map((line) => acceptLine(line)));
  };

  const readyTicket = async (ticket) => {
    const candidates = ticket.items.filter((line) => {
      const code = getStatusCode(line);
      return code === "SENT_TO_KITCHEN" || code === "PREPARING";
    });

    if (!candidates.length) {
      return;
    }

    const results = await Promise.all(candidates.map((line) => updateStatus(line, 4)));
    if (results.some((ok) => ok === false)) {
      return;
    }
  };

  const rawTickets = Array.isArray(data?.tickets) ? data.tickets : [];

  // Distinct kitchen sections/stations present across all active items.
  const stations = useMemo(() => {
    const set = new Set();
    rawTickets.forEach((ticket) => {
      (ticket.items || []).forEach((line) => {
        if (isKitchenActiveLine(line)) set.add(getCategoryName(line));
      });
    });
    return Array.from(set).sort();
  }, [rawTickets]);

  // Filter kitchen tickets by selected station names (category names).
  const tickets = useMemo(() => {
    const filtered = rawTickets
      .map((ticket) => {
        const filteredItems = (ticket.items || []).filter((line) => {
          if (!isKitchenActiveLine(line)) return false;
          if (!selectedStations.length) return true;
          return selectedStations.includes(getCategoryName(line));
        });

        if (!filteredItems.length) {
          return null;
        }

        return {
          ...ticket,
          items: filteredItems,
        };
      })
      .filter(Boolean);

    // Oldest ticket first: the longest wait is the most urgent.
    return filtered.sort(
      (a, b) =>
        msSince(b?.addedAt || b?.items?.[0]?.sentAt) -
        msSince(a?.addedAt || a?.items?.[0]?.sentAt)
    );
  }, [rawTickets, selectedStations]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <SoupKitchen /> Kitchen Display
          </h1>
          <p className={styles.subtitle}>
            {isLoading || isFetching
              ? "Loading kitchen tickets..."
              : `${tickets.length} active ticket${tickets.length === 1 ? "" : "s"}`}
          </p>
        </div>
      </div>

      {stations.length > 1 && (
        <div className={styles.stationTabs}>
          <button
            className={`${styles.stationTab} ${
              selectedStations.length === 0 ? styles.stationTabActive : ""
            }`}
            onClick={clearStations}
          >
            All
          </button>
          {stations.map((station) => (
            <button
              key={station}
              className={`${styles.stationTab} ${
                selectedStations.includes(station)
                  ? styles.stationTabActive
                  : ""
              }`}
              onClick={() => toggleStation(station)}
            >
              {station}
            </button>
          ))}
        </div>
      )}

      {tickets.length === 0 ? (
        <div className={styles.emptyState}>
          <DoneAll className={styles.emptyIcon} />
          <p>No pending kitchen orders. All caught up!</p>
        </div>
      ) : (
        <div className={styles.ticketGrid}>
          {tickets.map((ticket) => {
            const allReady = ticket.items.every(
              (l) => getStatusCode(l) === "READY"
            );
            const hasPending = ticket.items.some(
              (line) => getStatusCode(line) === "SENT_TO_KITCHEN"
            );
            return (
              <div
                key={`${ticket.tableId}-${ticket.orderHedId}`}
                className={`${styles.ticket} ${
                  allReady ? styles.ticketReady : ""
                }`}
              >
                <div className={styles.ticketHead}>
                  <div>
                    <span className={styles.ticketTable}>
                      Table {ticket.tableName || ticket.tableId}
                    </span>
                    <span className={styles.ticketInvoice}>
                      #{ticket.orderHedId}
                    </span>
                  </div>
                  <span className={styles.ticketTime}>
                    <Timer fontSize="inherit" /> {timeAgo(ticket.addedAt || ticket.items?.[0]?.sentAt)}
                  </span>
                </div>

                <div className={styles.ticketBody}>
                  {ticket.items.map((line) => {
                    const statusCode = getStatusCode(line);
                    return (
                    <div key={line.orderDetId} className={styles.kitchenLine}>
                      <div className={styles.kitchenLineInfo}>
                        <span className={styles.kitchenQty}>{line.qty}×</span>
                        <div className={styles.kitchenLineText}>
                          <span className={styles.kitchenName}>
                            {line.title}
                          </span>
                          {getCategoryName(line) && (
                            <span className={styles.kitchenStation}>
                              {getCategoryName(line)}
                            </span>
                          )}
                        </div>
                      </div>
                      {statusCode === "READY" ? (
                        <span className={styles.readyTag}>
                          <CheckCircle fontSize="inherit" /> Ready
                        </span>
                      ) : statusCode === "SENT_TO_KITCHEN" ? (
                        <div className={styles.lineActionGroup}>
                          <button
                            className={styles.acceptBtn}
                            onClick={() => acceptLine(line)}
                          >
                            Accept
                          </button>
                          <button
                            className={styles.lineReadyBtn}
                            onClick={() => readyLine(line)}
                          >
                            Mark Ready
                          </button>
                        </div>
                      ) : statusCode === "PREPARING" ? (
                        <button
                          className={styles.lineReadyBtn}
                          onClick={() => readyLine(line)}
                        >
                          Mark Ready
                        </button>
                      ) : (
                        <span className={styles.inProgressTag}>In Progress</span>
                      )}
                    </div>
                  );})}
                </div>

                <div className={styles.ticketActionRow}>
                  <button
                    className={styles.acceptAllBtn}
                    disabled={!hasPending}
                    onClick={() => {
                      acceptAllInTicket(ticket);
                    }}
                  >
                    <DoneAll fontSize="small" /> Accept All
                  </button>
                  <button
                    className={styles.ticketReadyBtn}
                    disabled={allReady}
                    onClick={() => {
                      readyTicket(ticket);
                    }}
                  >
                    <DoneAll fontSize="small" />
                    Ready to Serve
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KitchenPage;
